import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Award, Target, Eye, Heart, Users, Globe, Briefcase, Languages } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '../components/SEO';

const API = '/api';

const TEAM_IMAGE = 'https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwb2ZmaWNlfGVufDB8fHx8MTc3NDUyOTcxNnww&ixlib=rb-4.1.0&q=85';
const CONSULTING_IMAGE = 'https://images.unsplash.com/photo-1758873271761-6cfe9b4f000c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwb2ZmaWNlfGVufDB8fHx8MTc3NDUyOTcxNnww&ixlib=rb-4.1.0&q=85';

export const AboutPage = () => {
  const { t, language, direction } = useLanguage();
  const [pageContent, setPageContent] = useState({});

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API}/cms/page-content?sections=about_overview,about_mission_vision,about_values`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
        }
      } catch (error) {
        console.error('Error fetching page content:', error);
      }
    };
    fetchPageContent();
  }, []);

  // Helper to get CMS content with fallback to translations
  const getContent = (sectionKey, field, fallbackKey) => {
    const section = pageContent[sectionKey];
    if (section) {
      const content = language === 'ar' ? section.content_ar : section.content_en;
      if (content && content[field]) {
        return content[field];
      }
    }
    return fallbackKey ? t(fallbackKey) : '';
  };

  const values = [
    {
      icon: Award,
      title: getContent('about_values', 'value1_title', 'about.valuesList.excellence.title'),
      description: getContent('about_values', 'value1_desc', 'about.valuesList.excellence.description'),
    },
    {
      icon: Heart,
      title: getContent('about_values', 'value2_title', 'about.valuesList.integrity.title'),
      description: getContent('about_values', 'value2_desc', 'about.valuesList.integrity.description'),
    },
    {
      icon: Users,
      title: getContent('about_values', 'value3_title', 'about.valuesList.partnership.title'),
      description: getContent('about_values', 'value3_desc', 'about.valuesList.partnership.description'),
    },
    {
      icon: Target,
      title: getContent('about_values', 'value4_title', 'about.valuesList.innovation.title'),
      description: getContent('about_values', 'value4_desc', 'about.valuesList.innovation.description'),
    },
  ];

  const stats = [
    { value: '+900', label: language === 'ar' ? 'عملية توظيف ناجحة' : 'Placements Made' },
    { value: '+500', label: language === 'ar' ? 'برنامج تدريبي' : 'Training Programs' },
    { value: '+100', label: language === 'ar' ? 'عميل من الشركات' : 'Corporate Clients' },
    { value: '+50', label: language === 'ar' ? 'دولة نخدمها' : 'Countries Served' },
  ];

  const differentiators = [
    {
      icon: Briefcase,
      title: 'Industry Expertise',
      description: 'Deep knowledge across sectors including technology, finance, healthcare, and manufacturing.',
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Serving clients across Europe, Middle East, and international markets from our base in Germany.',
    },
    {
      icon: Award,
      title: 'Quality Assured',
      description: 'Accredited training programs and proven recruitment methodologies that deliver results.',
    },
    {
      icon: Languages,
      title: 'Bilingual Services',
      description: 'Full support in English and Arabic to serve our diverse international clientele.',
    },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'من نحن' : 'About Us' }
  ]);

  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO
        title="About Us - HR Company in Germany"
        titleAr="من نحن - شركة موارد بشرية في ألمانيا"
        description="WeDo HR Services UG is a Germany-based HR company serving Europe, Middle East, GCC, and Africa. We specialize in recruitment, training, HR consulting, and career advisory services."
        descriptionAr="ويدو لخدمات الموارد البشرية شركة ألمانية تخدم أوروبا والشرق الأوسط ودول الخليج وأفريقيا. نتخصص في التوظيف والتدريب واستشارات الموارد البشرية والإرشاد المهني."
        keywords="about WeDo, HR company Germany, recruitment agency Europe, training provider Middle East, HR consulting, عن ويدو, شركة موارد بشرية"
        canonicalUrl="/about"
        structuredData={breadcrumbSchema}
        language={language}
      />
      {/* Hero Section */}
      <section className="bg-white border-b border-[#E5E7EB] py-20">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] mb-6">
              {getContent('about_overview', 'title', 'about.title')}
            </h1>
            <p className="text-xl text-[#6B7280]">
              {getContent('about_overview', 'subtitle', 'about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">
                {getContent('about_overview', 'overview_title', 'about.overview')}
              </h2>
              <p className="text-lg text-[#6B7280] leading-relaxed">
                {getContent('about_overview', 'overview_text', 'about.overviewText')}
              </p>
            </div>
            <div>
              <img 
                src={TEAM_IMAGE} 
                alt="Our team" 
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#33AADD]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl sm:text-5xl font-bold text-white mb-2">{stat.value}</p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="card-hover">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center mb-6">
                  <Target className="h-7 w-7 text-[#33AADD]" />
                </div>
                <h3 className="text-2xl font-bold text-[#111827] mb-4">
                  {getContent('about_mission_vision', 'mission_title', 'about.mission')}
                </h3>
                <p className="text-[#6B7280] leading-relaxed">
                  {getContent('about_mission_vision', 'mission_text', 'about.missionText')}
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center mb-6">
                  <Eye className="h-7 w-7 text-[#33AADD]" />
                </div>
                <h3 className="text-2xl font-bold text-[#111827] mb-4">
                  {getContent('about_mission_vision', 'vision_title', 'about.vision')}
                </h3>
                <p className="text-[#6B7280] leading-relaxed">
                  {getContent('about_mission_vision', 'vision_text', 'about.visionText')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
              {getContent('about_values', 'title', 'about.values')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="card-hover text-center" data-testid={`value-${index}`}>
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center mx-auto mb-5">
                    <value.icon className="h-7 w-7 text-[#33AADD]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#111827] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-[#6B7280] text-sm">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why WeDo Section */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src={CONSULTING_IMAGE} 
                alt="Consulting" 
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">
                {t('whyWedo.title')}
              </h2>
              <p className="text-lg text-[#6B7280] mb-8">
                {t('whyWedo.subtitle')}
              </p>
              <div className="space-y-6">
                {differentiators.map((item, index) => (
                  <div key={index} className="flex gap-4" data-testid={`differentiator-${index}`}>
                    <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-[#33AADD]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#111827] mb-1">{item.title}</h4>
                      <p className="text-sm text-[#6B7280]">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
